import React, { useEffect, useState } from "react";
import { FileText, Home, LogOut, Settings } from "lucide-react";

import { User } from "../../interfaces/user.interface";
import { SERVICES_BASE_URL, VOICE_URL, WS_CHAT_URL } from "../../../../infra/settings";
import {
  Brand,
  BrandIcon,
  BrandLogo,
  BrandText,
  ChatContainer,
  ControlsSection,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  EmptyStateTitle,
  Header,
  HeaderContent,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  LogoutButton,
  MainContent,
  NavLink,
  NavLinks,
  SelectIcon,
  SelectLabel,
  SelectWrapper,
  StyledSelect,
  UserAvatar,
  UserEmail,
  UserInfo,
  UserName,
  UserSection,
  WidgetContainer,
} from "./style";

interface InfinityWidgetProps extends
  React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLElement>,
    HTMLElement
  > {
  id?: string;
  title?: string;
  theme?: string;
  lang?: string;
  "display-mode"?: string;
  source?: string;
  fullscreen?: boolean;
  protocol?: string;
  "websocket-url"?: string;
}
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "infinity-widget": InfinityWidgetProps;
    }
  }
}

interface ChatComponentProps {
  user: User;
  onLogout: () => void;
}

export const ChatComponent = ({ user, onLogout }: ChatComponentProps) => {
  const [handlers, setHandlers] = useState<string[]>([]);
  const [selectedHandler, setSelectedHandler] = useState(
    () => localStorage.getItem("handler") ?? "common",
  );
  const [isLoadingHandlers, setIsLoadingHandlers] = useState(true);
  const [widgetReady, setWidgetReady] = useState(false);
  const [sessionUUID] = useState(() => crypto.randomUUID());

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${SERVICES_BASE_URL}/v1/handlers`);
        const json = await res.json();
        setHandlers(Array.isArray(json) ? json : json?.handlers ?? []);
      } catch (err) {
        console.error("Handlers fetch error:", err);
        setHandlers([]);
      } finally {
        setIsLoadingHandlers(false);
      }
    })();
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "module";
    script.src =
      "https://copilot-infinity-widget.storage.googleapis.com/lib/infinity-widget.esm.js";

    script.onload = () => setWidgetReady(true);

    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
      setWidgetReady(false);
    };
  }, []);

  useEffect(() => {
    if (!widgetReady) return;
    const el = document.getElementById("chatbot") as any;
    if (el && !el.__patched) {
      el.attributeChangedCallback = () => {};
      el.__patched = true;
    }
  }, [widgetReady, selectedHandler]);

  const chooseHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const h = e.target.value;
    setSelectedHandler(h);
    localStorage.setItem("handler", h);
  };

  const logout = () => {
    onLogout();
  };

  if (isLoadingHandlers) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Loading chat interface…</LoadingText>
      </LoadingContainer>
    );
  }

  const wsURL = `${WS_CHAT_URL}/websocket/${sessionUUID}`;

  return (
    <ChatContainer>
      <Header>
        <HeaderContent>
          <Brand>
            <BrandIcon>
              <BrandLogo src="/logo.png" />
            </BrandIcon>
            <BrandText>Infinity Copilot</BrandText>
          </Brand>

          <NavLinks>
            <NavLink href="/">
              <Home size={18} />
              Home
            </NavLink>
            <NavLink
              href="https://docs.infinity-copilot.com/"
              target="_blank"
            >
              <FileText size={18} />
              Documentation
            </NavLink>
          </NavLinks>

          <UserSection>
            <UserAvatar src={user.picture} alt={user.name} />
            <UserInfo>
              <UserName>{user.name}</UserName>
              <UserEmail>{user.email}</UserEmail>
            </UserInfo>
            <LogoutButton onClick={logout} title="Logout">
              <LogOut size={18} />
            </LogoutButton>
          </UserSection>
        </HeaderContent>
      </Header>

      <MainContent>
        <ControlsSection>
          <SelectWrapper>
            <SelectLabel>
              <Settings size={16} />
              Choose Handler
            </SelectLabel>
            <StyledSelect value={selectedHandler} onChange={chooseHandler}>
              {handlers.length === 0 && <option value="common">common</option>}
              {handlers.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </StyledSelect>
            <SelectIcon>
              <Settings size={16} />
            </SelectIcon>
          </SelectWrapper>
        </ControlsSection>

        <WidgetContainer>
          {widgetReady && selectedHandler
            ? (
              <infinity-widget
                key={selectedHandler}
                id="chatbot"
                title="Infinity Copilot"
                theme="dark"
                lang="pt"
                fullscreen
                display-mode="embedded"
                source={selectedHandler}
                protocol="websocket"
                websocket-url={wsURL}
                data-user-email={user.email}
                voice-mode
                voice-url={VOICE_URL}
              />
            )
            : (
              <EmptyState>
                <EmptyStateIcon>
                  <Settings size={64} />
                </EmptyStateIcon>
                <EmptyStateTitle>Select a Handler</EmptyStateTitle>
                <EmptyStateText>
                  Choose a handler from the dropdown above to start chatting
                  with your AI assistant.
                </EmptyStateText>
              </EmptyState>
            )}
        </WidgetContainer>
      </MainContent>
    </ChatContainer>
  );
};
